/** @jsx jsx */
import { Fragment } from 'react'
import { css, jsx } from '@emotion/core'
import { List, ListItem, Link, Box, Flex, Icon } from '@chakra-ui/core'
import RouterLink from 'next/link'
import GridContainer from './GridContainer'
import GridItem from './GridItem'
import { MenuItems as MenuItemsType } from 'types'
import { FiExternalLink, FiArrowRight } from 'react-icons/fi'
import l10n from 'l10n'

const MenuItems = ({ menuItems, arrow = true }) => {
  if (!menuItems) return <></>

  return menuItems?.map((item) => {
    if (item.typeHandle === 'url') {
      return (
        <ListItem my={2} key={item.uid}>
          <MenuItemUrlLink arrow={arrow} item={item}>
            {item.linkTitle}
          </MenuItemUrlLink>
        </ListItem>
      )
    }
    return item?.pageLink?.map((link) => (
      <ListItem my={2} key={link.uid}>
        <MenuItemPageLink
          arrow={arrow}
          link={link}
          openInNewWindow={item.openInNewWindow}
        >
          {link.title}
        </MenuItemPageLink>
      </ListItem>
    ))
  })
}

const MenuItemPageLink = ({ children, link, openInNewWindow, arrow }: any) => {
  return (
    <RouterLink href="/[...uri]" as={link.url} passHref>
      <Link color="white" fontWeight="bold" fontSize="md">
        {children}
        {openInNewWindow ? (
          <Box as={FiExternalLink} display="inline" mx={1} />
        ) : (
          arrow && (
            <Box as={FiArrowRight} color="white" display="inline" mx={1} />
          )
        )}
      </Link>
    </RouterLink>
  )
}

const MenuItemUrlLink = ({ children, item, arrow }: any) => {
  return (
    <Link
      isExternal={item.openInNewWindow}
      href={item.linkUrl}
      color="white"
      fontWeight="bold"
      fontSize="md"
    >
      {children}
      {item.openInNewWindow ? (
        <Box as={FiExternalLink} display="inline" mx={1} />
      ) : (
        arrow && <Box as={FiArrowRight} color="white" display="inline" mx={1} />
      )}
    </Link>
  )
}

const Footer = ({ uid, menuItems, submenuItems, services }: Props) => {
  if (!menuItems) return <Fragment key={uid}></Fragment>
  return (
    <Box as="footer" key={uid} backgroundColor="red.900">
      <GridContainer
        alignItems="start"
        // @ts-ignore
        px={6}
        py={16}
      >
        <GridItem colSpan={[12, 12, 12, 4]}>
          <Box as="h2" color="red.200" mb={4}>
            {l10n.ourServices}
          </Box>
          <List spacing={[4, 5]}>
            {services?.map((service) => (
              <ListItem key={service.uid}>
                <RouterLink href="/[...uri]" as={service.url} passHref>
                  <Link
                    fontFamily="serif"
                    color="white"
                    fontWeight="bold"
                    fontSize={['md', null, null, 'lg']}
                  >
                    <Box
                      // @ts-ignore
                      as="img"
                      // @ts-ignore
                      src={service.navigationalSymbol?.[0].url}
                      width={6}
                      height={6}
                      mr={4}
                      mt={['-2px', '-4px']}
                      color="red.600"
                      display="inline"
                      css={css`
                        filter: brightness(160%) contrast(96%);
                      `}
                    />
                    {service.title}
                  </Link>
                </RouterLink>
              </ListItem>
            ))}
          </List>
        </GridItem>
        <GridItem colSpan={[12, 12, 12, 4]} mt={[10, 10, 10, 0]}>
          <Box as="h2" color="red.200" mb={4}>
            {l10n.oftenUsedPages}
          </Box>
          <List>
            <MenuItems menuItems={menuItems} />
          </List>
          <List my={12}>
            <MenuItems menuItems={submenuItems} arrow={false} />
          </List>
        </GridItem>

        <GridItem
          colSpan={[12, 12, 12, 4]}
          // @ts-ignore
          // display={['none', 'none', 'none', 'block']}
        >
          <Box as="h2" color="red.200" mb={4}>
            {l10n.contactUs}
          </Box>

          <Box as="section" color="white" mb={8}>
            <Box fontWeight="bold">Service Centre</Box>
            <a href="#">Teorifagbygget Hus 2 9037 tromsø</a>
          </Box>

          <Box as="section" color="white" mb={8}>
            <Box fontWeight="bold">{l10n.contactInfo}</Box>
            <Box>
              Tlf <a href={`tel:${'+47 77 64 90 00'}`}>+47 77 64 90 00</a>
            </Box>
            <Box>
              <a href="mailto:post@samskipnaden.no">post@samskipnaden.no</a>
            </Box>
            <Box>
              <a href="mailto:bolig@samskipnaden.no">bolig@samskipnaden.no</a>
            </Box>
          </Box>
        </GridItem>
      </GridContainer>

      <Box backgroundColor="brown.100">
        <Flex
          maxWidth="7xl"
          margin="0 auto"
          width="100%"
          justifyContent="space-between"
          fontSize="lg"
          px={6}
          py={[4, 8]}
          alignItems="center"
        >
          <Flex fontSize={['sm', 'md']}>
            <Box color="red.700" mr={4}>
              {l10n.language}
            </Box>
            <Link color="red.900" fontWeight="bold" href="/" mr={4}>
              Norsk
            </Link>
            <Link color="red.900" fontWeight="bold" href="/en" mr={4}>
              English
            </Link>
          </Flex>
          <Flex>
            <a
              href="https://www.facebook.com/samskipnadenTromso/"
              aria-label="Facebook"
            >
              <Icon name="nasFacebook" color="red.900" size="1.5rem" mr={3} />
            </a>
            <a
              href="https://www.instagram.com/samskipnaden_tromso/"
              aria-label="Instagram"
            >
              <Icon name="nasInstagram" color="red.900" size="1.5rem" />
            </a>
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}
type Props = {
  uid: string
  services?: [
    {
      uid: string
      url: string
      title: string
      slug: string
    }
  ]
  submenuItems?: [MenuItemsType]
  menuItems: [MenuItemsType]
}

export default Footer
